/**
 * A2-6: central handling of an expired/invalid session (a 401 anywhere).
 *
 * We discard the client-side session state, route to `/login`, and remember the
 * return target as `?redirect=<href>` so a successful login can send the user
 * back. The "Sitzung abgelaufen" hint is only shown when the user actually had a
 * session — this keeps it off the initial unauthenticated load and off a wrong
 * password on the login form (both of which surface a 401 but are not
 * expirations, and have their own UI).
 */
import type { QueryClient } from '@tanstack/react-query';
import type { AnyRouter } from '@tanstack/react-router';
import { getAuthControllerMeQueryKey } from '@werkbank/shared';
import { notify } from './notify';

export function redirectToLogin(router: AnyRouter, queryClient: QueryClient): void {
  const location = router.state.location;
  // Already on the login screen (e.g. a failed login attempt): the form shows
  // its own error, nothing to redirect or announce.
  if (location.pathname === '/login') return;

  const hadSession = queryClient.getQueryData(getAuthControllerMeQueryKey()) != null;

  // Discard every cached authenticated response. Clearing also deduplicates a
  // burst of simultaneous 401s: after the first, `hadSession` is false for the
  // rest, so we redirect and announce exactly once.
  queryClient.clear();

  if (hadSession) {
    notify.info('Sitzung abgelaufen, bitte erneut anmelden.', 'Abgemeldet');
  }

  void router.navigate({ to: '/login', search: { redirect: location.href } });
}
